import type { ContractProduct } from './ContractDetailsDrawer';
import { formatPeriodDate } from './format';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const CURRENCY_FORMAT = '$#,##0.00';

/**
 * Builds and downloads an .xlsx mirroring the drawer / PDF export:
 * title, dealer, export date, then Product | Period | Gross Budget rows
 * (latest period per product) and a bold Total row.
 *
 * exceljs is imported dynamically so it is only fetched when the user
 * actually exports (keeps it out of the initial static-export bundle).
 */
export async function exportContractToExcel(products: ContractProduct[], dealerName?: string) {
    const { Workbook } = await import('exceljs');

    const workbook = new Workbook();
    const sheet = workbook.addWorksheet('Contract Details');

    sheet.getColumn(1).width = 36;
    sheet.getColumn(2).width = 30;
    sheet.getColumn(3).width = 16;

    const titleRow = sheet.addRow(['Current Contract Details']);
    titleRow.font = { bold: true, size: 14 };

    if (dealerName) {
        const dealerRow = sheet.addRow([dealerName]);
        dealerRow.font = { bold: true, size: 12 };
    }

    const exportDate = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const dateRow = sheet.addRow([`Exported ${exportDate}`]);
    dateRow.font = { size: 10, color: { argb: 'FF8A9199' } };

    sheet.addRow([]);

    const headerRow = sheet.addRow(['Product', 'Period', 'Gross Budget']);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
        cell.border = { bottom: { style: 'thin', color: { argb: 'FFE4E8EA' } } };
    });

    let totalBudget = 0;
    for (const product of products) {
        const period = product.periods[product.periods.length - 1];
        totalBudget += period.grossBudget;
        const row = sheet.addRow([product.name, formatPeriodDate(period.period), period.grossBudget]);
        row.getCell(3).numFmt = CURRENCY_FORMAT;
    }

    const totalRow = sheet.addRow(['Total', '', totalBudget]);
    totalRow.font = { bold: true };
    totalRow.getCell(3).numFmt = CURRENCY_FORMAT;
    totalRow.eachCell((cell) => {
        cell.border = { top: { style: 'thin', color: { argb: 'FF2D3A3F' } } };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: EXCEL_MIME });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = dealerName
        ? `contract_${dealerName.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`
        : 'contract_details.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
